// src/stores/brainStore.ts
// Phase 6.6 Bundle 3.1 - Brain panel UI state.
//
// In-memory only (no persist middleware). Panel open/close and the
// currently-active conversation are session-scoped concerns; nothing
// here belongs in localStorage.
//
// Pairs with:
//   - useBrainQueries.ts (server-side conversation data via TanStack Query)
//   - BrainPanel.tsx (consumes isOpen, close)
//   - Sidebar/Header (call open() to trigger the panel)

import { create } from "zustand";

interface BrainState {
  isOpen: boolean;
  activeConversationId: string | null;

  // Open the panel. Optional conversationId resumes a specific conversation;
  // omit it to open with no active conversation (new conversation state).
  open: (conversationId?: string | null) => void;

  // Close the panel without clearing activeConversationId, so reopening
  // resumes the same conversation by default.
  close: () => void;

  // Explicitly switch the active conversation (e.g. from the history page).
  setActiveConversationId: (id: string | null) => void;

  // Reset to a fresh "New conversation" state. Used by the New Conversation
  // button inside the panel.
  startNewConversation: () => void;
}

export const useBrainStore = create<BrainState>()((set) => ({
  isOpen: false,
  activeConversationId: null,

  open: (conversationId) =>
    set({
      isOpen: true,
      activeConversationId: conversationId ?? null,
    }),

  close: () => set({ isOpen: false }),

  setActiveConversationId: (id) => set({ activeConversationId: id }),

  startNewConversation: () => set({ activeConversationId: null }),
}));